import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import SEO from "../components/seo/SEO";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, Phone, Volume2, Lightbulb, Package, Settings2, Monitor, Layers, Cpu, Wrench, Star, Zap } from "lucide-react";
import Footer from "../components/landing/Footer";
import Navigation from "../components/landing/Navigation";
import ModelViewer from "../components/stage/ModelViewer";
import { api, BASE } from "../services/api";

const ICON_MAP = { Volume2, Lightbulb, Package, Settings2, Monitor, Layers, Cpu, Wrench, Star, Zap };

function resolveImg(url) {
    if (!url) return "";
    return url.startsWith("http") ? url : `${BASE}${url}`;
}

// ── Feature card ─────────────────────────────────────────────────────────────
function FeatureCard({ feature, accent, faint, border, index }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: index * 0.07 }}
            className="flex gap-4 p-5 rounded-xl border bg-nova-surface/40 transition-all hover:bg-nova-surface/70"
            style={{ borderColor: "rgba(255,255,255,0.07)" }}
            onMouseEnter={e => e.currentTarget.style.borderColor = border}
            onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)"}
        >
            <div className="mt-0.5 shrink-0">
                <CheckCircle2 className="w-4 h-4" style={{ color: accent }} />
            </div>
            <div>
                <div className="font-heading font-bold text-white text-sm mb-1">{feature.title}</div>
                <div className="text-zinc-400 text-sm leading-relaxed">{feature.desc}</div>
            </div>
        </motion.div>
    );
}

// ── Photo gallery ─────────────────────────────────────────────────────────────
function PhotoGallery({ images, accent }) {
    const [active, setActive] = useState(null);

    useEffect(() => {
        if (active === null) return;
        const onKey = (e) => {
            if (e.key === "Escape") setActive(null);
            if (e.key === "ArrowRight") setActive(i => (i + 1) % images.length);
            if (e.key === "ArrowLeft")  setActive(i => (i - 1 + images.length) % images.length);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [active, images]);

    if (!images || !images.length) return null;
    return (
        <section>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {images.map((img, i) => (
                    <motion.button
                        key={i}
                        onClick={() => setActive(i)}
                        initial={{ opacity: 0, scale: 0.96 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: i * 0.07 }}
                        className={`relative overflow-hidden rounded-2xl border border-white/6 group cursor-zoom-in ${i === 0 ? "sm:col-span-2 aspect-[16/7]" : "aspect-video"}`}
                    >
                        <img
                            src={resolveImg(img)} alt={`Fotka ${i + 1}`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                            loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-nova-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400" />
                        <div
                            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-400"
                            style={{ boxShadow: `inset 0 0 0 1px ${accent}40` }}
                        />
                    </motion.button>
                ))}
            </div>

            {/* Lightbox */}
            {active !== null && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
                    onClick={() => setActive(null)}
                >
                    {/* Prev */}
                    <button
                        className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                        onClick={e => { e.stopPropagation(); setActive(i => (i - 1 + images.length) % images.length); }}
                    >
                        ‹
                    </button>
                    {/* Image */}
                    <img
                        src={resolveImg(images[active])}
                        alt={`Fotka ${active + 1}`}
                        className="max-w-[90vw] max-h-[90vh] object-contain rounded-xl shadow-2xl"
                        onClick={e => e.stopPropagation()}
                    />
                    {/* Next */}
                    <button
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                        onClick={e => { e.stopPropagation(); setActive(i => (i + 1) % images.length); }}
                    >
                        ›
                    </button>
                    {/* Close */}
                    <button
                        className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white text-lg transition-colors"
                        onClick={() => setActive(null)}
                    >
                        ×
                    </button>
                    {/* Counter */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 font-mono text-xs text-zinc-400">
                        {active + 1} / {images.length}
                    </div>
                </div>
            )}
        </section>
    );
}

// ── Gallery strip from references ─────────────────────────────────────────────
function GalleryStrip({ category, accent }) {
    const [items, setItems] = useState([]);
    useEffect(() => {
        api.getReferencie()
            .then(all => setItems(all.filter(i => i.category === category).slice(0, 4)))
            .catch(() => {});
    }, [category]);

    if (!items.length) return null;
    return (
        <section>
            <motion.div
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5 }}
                className="mb-8"
            >
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] mb-3" style={{ color: accent }}>/ Referencie</div>
                <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">Z našich realizácií</h2>
            </motion.div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {items.map((it, i) => (
                    <motion.div
                        key={it.id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.35, delay: i * 0.06 }}
                        className="aspect-video relative overflow-hidden rounded-xl border border-white/6 group"
                    >
                        <img src={resolveImg(it.image_url)} alt={it.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                        <div className="absolute inset-0 bg-gradient-to-t from-nova-black/80 via-transparent to-transparent" />
                        <div className="absolute bottom-2 left-3 right-3">
                            <div className="text-white text-xs font-heading font-bold truncate">{it.title}</div>
                        </div>
                    </motion.div>
                ))}
            </div>
            <div className="mt-4 text-right">
                <Link to="/referencie" className="inline-flex items-center gap-1.5 text-sm font-mono group" style={{ color: accent }}>
                    Všetky referencie
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </Link>
            </div>
        </section>
    );
}

// ── Other services row ────────────────────────────────────────────────────────
function OtherServices({ currentSlug }) {
    const [others, setOthers] = useState([]);
    useEffect(() => {
        api.getSluzby()
            .then(all => setOthers(all.filter(s => s.slug !== currentSlug).slice(0, 3)))
            .catch(() => {});
    }, [currentSlug]);

    if (!others.length) return null;
    return (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {others.map((s, i) => {
                const Icon = ICON_MAP[s.icon_name] || Layers;
                return (
                    <motion.div
                        key={s.slug}
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: i * 0.08 }}
                    >
                        <Link
                            to={`/sluzby/${s.slug}`}
                            className="group flex items-center gap-4 p-4 rounded-xl border border-white/6 bg-nova-surface/30 hover:border-white/18 hover:bg-nova-surface/60 transition-all"
                        >
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                                style={{ background: s.faint, border: `1px solid ${s.border}` }}>
                                <Icon className="w-4 h-4" style={{ color: s.accent }} strokeWidth={1.6} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-heading font-bold text-white text-sm truncate">{s.title}</div>
                                <div className="text-zinc-500 text-xs truncate">{s.tagline}</div>
                            </div>
                            <ArrowRight className="w-4 h-4 text-zinc-700 group-hover:text-white group-hover:translate-x-0.5 transition-all shrink-0" />
                        </Link>
                    </motion.div>
                );
            })}
        </div>
    );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────
function Skeleton() {
    return (
        <div className="min-h-screen bg-nova-black text-white animate-pulse">
            <div className="h-16 bg-nova-surface/30 border-b border-white/5" />
            <div className="h-80 bg-nova-surface/20" />
            <div className="max-w-7xl mx-auto px-6 sm:px-12 py-16 space-y-8">
                {[1,2,3].map(n => <div key={n} className="h-32 bg-nova-surface/20 rounded-xl" />)}
            </div>
        </div>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ServiceDetailPage() {
    const { slug } = useParams();
    const [svc, setSvc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        if (!slug) return;
        setLoading(true);
        setNotFound(false);
        api.getSluzba(slug)
            .then(data => { setSvc(data); setLoading(false); })
            .catch(() => { setNotFound(true); setLoading(false); });
    }, [slug]);

    if (loading) return <Skeleton />;
    if (notFound || !svc) return <Navigate to="/" replace />;

    const IconComponent = ICON_MAP[svc.icon_name] || Layers;
    const accent = svc.accent || "#0099ff";
    const faint  = svc.faint  || "rgba(0,153,255,0.08)";
    const border = svc.border || "rgba(0,153,255,0.25)";
    const heroImg = resolveImg(svc.hero_image);

    const serviceJsonLd = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": svc.title,
        "description": svc.tagline || svc.desc,
        "url": `https://kbmusic.sk/sluzby/${slug}`,
        "image": heroImg || "https://kbmusic.sk/logo-kb.png",
        "provider": {
            "@type": "LocalBusiness",
            "@id": "https://kbmusic.sk",
            "name": "KB MUSIC",
            "url": "https://kbmusic.sk",
            "email": "info@kbmusic.sk",
            "areaServed": { "@type": "Country", "name": "Slovensko" },
        },
        "areaServed": { "@type": "Country", "name": "Slovensko" },
        "inLanguage": "sk-SK",
    };

    return (
        <div className="min-h-screen bg-nova-black text-white">
            <SEO
                title={svc.title}
                description={svc.tagline ? `${svc.tagline} ${svc.desc ? "— " + svc.desc.slice(0, 100) : ""}`.trim() : svc.desc?.slice(0, 160)}
                canonical={`/sluzby/${slug}`}
                ogImage={heroImg || undefined}
                ogType="website"
                jsonLd={serviceJsonLd}
            />
            <Navigation />

            {/* Hero */}
            <div className="relative overflow-hidden border-b border-white/5 min-h-[420px] sm:min-h-[500px] flex items-end pt-20">
                {heroImg ? (
                    <>
                        <img
                            src={heroImg} alt={svc.title}
                            className="absolute inset-0 w-full h-full object-cover object-center"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-nova-black via-nova-black/70 to-nova-black/30" />
                        <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse 60% 50% at 70% 30%, ${faint.replace("0.08","0.18")}, transparent)` }} />
                    </>
                ) : (
                    <>
                        <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse 70% 60% at 60% 40%, ${faint.replace("0.08","0.15")}, transparent)` }} />
                        <div className="absolute inset-0 bg-[radial-gradient(ellipse_100%_60%_at_50%_100%,rgba(0,0,0,0.6),transparent)]" />
                    </>
                )}

                <div className="relative w-full max-w-7xl mx-auto px-6 sm:px-12 py-16 sm:py-20">
                    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                        {/* Breadcrumb */}
                        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-6">
                            <Link to="/" className="hover:text-zinc-300 transition-colors">Domov</Link>
                            <span>/</span>
                            <Link to="/#services" className="hover:text-zinc-300 transition-colors">Služby</Link>
                            <span>/</span>
                            <span style={{ color: accent }}>{svc.title}</span>
                        </div>

                        {/* Icon + label */}
                        <div className="flex items-center gap-3 mb-5">
                            <div className="w-12 h-12 rounded-xl flex items-center justify-center"
                                style={{ background: faint, border: `1px solid ${border}` }}>
                                <IconComponent className="w-5 h-5" style={{ color: accent }} strokeWidth={1.6} />
                            </div>
                            <span className="font-mono text-xs uppercase tracking-[0.2em]" style={{ color: accent }}>/ Služba</span>
                        </div>

                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter leading-[1.02] mb-4">
                            {svc.title}
                        </h1>
                        <p className="text-xl sm:text-2xl font-heading font-medium mb-6" style={{ color: accent }}>
                            {svc.tagline}
                        </p>
                        <p className="text-zinc-300 text-lg max-w-2xl leading-relaxed mb-10">
                            {svc.desc}
                        </p>

                        {/* Stats */}
                        <div className="flex flex-wrap gap-8">
                            {(svc.stats || []).map((s, i) => (
                                <div key={i} className="flex flex-col">
                                    <span className="font-heading text-2xl sm:text-3xl font-black text-white">{s.val}</span>
                                    <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-400">{s.label}</span>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </div>

            <main className="max-w-7xl mx-auto px-6 sm:px-12 py-16 space-y-20">

                {/* Features */}
                <section>
                    <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="mb-8">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] mb-3" style={{ color: accent }}>/ Čo zahŕňa táto služba</div>
                        <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">Čo dostanete</h2>
                    </motion.div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {(svc.features || []).map((f, i) => (
                            <FeatureCard key={i} feature={f} index={i} accent={accent} faint={faint} border={border} />
                        ))}
                    </div>
                </section>

                {/* 3D Models — shown right after features */}
                {(() => {
                    const list = svc.models3d?.length > 0
                        ? svc.models3d
                        : svc.model3d_url
                            ? [{ title: "Interaktívny 3D model", desc: "", url: svc.model3d_url }]
                            : [];
                    if (!list.length) return null;
                    return list.map((m, i) => (
                        <motion.section
                            key={i}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: i * 0.08 }}
                        >
                            <div className="mb-6">
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] mb-3" style={{ color: accent }}>
                                    / Interaktívny 3D model {list.length > 1 ? `${i + 1}/${list.length}` : ""}
                                </div>
                                {m.title && (
                                    <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter mb-2">
                                        {m.title}
                                    </h2>
                                )}
                                {m.desc && (
                                    <p className="text-zinc-400 text-base leading-relaxed max-w-2xl">{m.desc}</p>
                                )}
                            </div>
                            <div className="rounded-2xl overflow-hidden border border-white/8 bg-nova-surface/20" style={{ height: "520px" }}>
                                <ModelViewer fileUrl={m.url} />
                            </div>
                            <p className="mt-3 text-zinc-600 text-xs font-mono text-center">
                                Otáčajte myšou · Zoom kolieskom · Pravý klik = posun
                            </p>
                        </motion.section>
                    ));
                })()}

                {/* Use cases */}
                <section>
                    <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="mb-7">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] mb-3" style={{ color: accent }}>/ Vhodné pre</div>
                        <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">Pre aké eventy</h2>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }}
                        className="flex flex-wrap gap-2.5"
                    >
                        {(svc.useCases || []).map((uc, i) => (
                            <span key={i}
                                className="px-4 py-2 rounded-full text-sm font-medium border"
                                style={{ background: faint, color: accent, borderColor: border }}>
                                {uc}
                            </span>
                        ))}
                    </motion.div>
                </section>


                {/* Key equipment */}
                <section>
                    <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="mb-8">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] mb-3" style={{ color: accent }}>/ Technika</div>
                        <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">Kľúčové zariadenia</h2>
                    </motion.div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {(svc.equipment || []).map((eq, i) => (
                            <motion.div key={i}
                                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.1 }}
                                className="p-5 rounded-xl border bg-nova-surface/40"
                                style={{ borderColor: border.replace("0.25", "0.15") }}
                            >
                                <div className="font-mono text-[9px] uppercase tracking-wider mb-2" style={{ color: accent }}>{eq.brand}</div>
                                <div className="font-heading font-black text-white text-lg leading-tight mb-1">{eq.model}</div>
                                <div className="text-zinc-500 text-sm">{eq.detail}</div>
                            </motion.div>
                        ))}
                    </div>
                    <div className="mt-4 text-right">
                        <Link to="/technika" className="inline-flex items-center gap-1.5 text-sm font-mono group" style={{ color: accent }}>
                            Celý inventár techniky
                            <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                        </Link>
                    </div>
                </section>

                {/* Service photo gallery */}
                <PhotoGallery images={svc.images} accent={accent} />

                {/* Gallery strip from references */}
                <GalleryStrip category={svc.gallery} accent={accent} />

                {/* CTA */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
                    className="rounded-2xl p-8 sm:p-12 border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6"
                    style={{ background: faint, borderColor: border }}
                >
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] mb-3" style={{ color: accent }}>/ Máte záujem?</div>
                        <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter text-white mb-2">
                            Dopytujte túto službu
                        </h2>
                        <p className="text-zinc-400 text-sm max-w-lg">
                            Napíšte nám a do 24 hodín vám zašleme nezáväznú cenovú ponuku šitú na mieru vášho eventu.
                        </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                        <Link to="/#contact"
                            className="inline-flex items-center gap-2 h-11 px-6 font-semibold text-sm rounded-md transition-all hover:saturate-125"
                            style={{ background: accent, color: "#000" }}>
                            <Phone className="w-4 h-4" /> Kontaktovať nás
                        </Link>
                        <Link to="/vizualizacie"
                            className="inline-flex items-center justify-center h-11 px-6 border text-white text-sm font-medium rounded-md hover:bg-white/5 transition-all"
                            style={{ borderColor: border }}>
                            Pozrieť 3D návrhy
                        </Link>
                    </div>
                </motion.section>

                {/* Other services */}
                <section>
                    <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="mb-7">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-nova-cyan mb-3">/ Ďalšie služby</div>
                        <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">Môže vás zaujímať aj</h2>
                    </motion.div>
                    <OtherServices currentSlug={slug} />
                </section>
            </main>

            <Footer />
        </div>
    );
}
